import {DescriptionRounded, LibraryAddRounded} from "@mui/icons-material";
import {Button, Chip, Grid, IconButton, MenuItem, TextField, Tooltip} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import '../../style/SalesMarketing.css';
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import {useRecoilState, useRecoilValue} from "recoil";
import {CATEGORY_LIST, PRODUCT_STATUS_LIST, SEGMENT_LIST} from "../../service/SalesMarketingRecoil";
import * as Yup from 'yup';
import AdvancedFilter from "../../component/AdvancedFilter";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {useStatusValuesProduct} from "../../component/useStatusValuesProduct";
import Stack from "@mui/material/Stack";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";
import EmptyState from "../../../../util/component/EmptyState";
import ProductCodeDetailModal from "./modals/ProductCodeDetailModal";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {useHasScope} from "../../../../config/globalConfig";


const ProductCodeListPage = () => {
    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const navigate = useNavigate();
    const productCodeActions = useProductCodeActions();
    const [filters, setFilters] = useState<any>({
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        status: '',
        segment: '',
        category: '',
        productCode: '',
        name: ''
    });
    const [searchInput, setSearchInput] = useState("");
    const [segments] = useRecoilState(SEGMENT_LIST);
    const [categories] = useRecoilState(CATEGORY_LIST);
    const [productStatusList] = useRecoilState(PRODUCT_STATUS_LIST);
    const gridRef = useRef<AgGridReact>(null);
    const {getStatusColorProduct, getStatusLabelProduct} = useStatusValuesProduct();
    const [productCodeToPass, setProductCodeToPass] = useState<any>({});
    const [openDetail, setOpenDetail] = useState<boolean>(false);
    const hasScopes= useHasScope;

    useEffect(() => {
        productCodeActions.listCategory();
        productCodeActions.listSegment();
        productCodeActions.listProductStatus();
        productCodeActions.listPaymentTimings();
        productCodeActions.listBillingModes();
    }, []);

    const validationObj = {
        status: Yup.string().nullable(),
        segment: Yup.string().nullable(),
        category: Yup.string().nullable(),
        productCode: Yup.string().nullable(),
        name: Yup.string().nullable()
    }

    const defaultFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        status: "",
        segment: "",
        category: "",
        productCode: '',
        name: ''
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            },
        },
    };

    const [categoryValues, setCategoryValues] = useState<any>("");
    const [segmentValue, setSegmentValue] = useState<any>("");

    const fields = {
        status: (field: any) => (
            <TextField
                fullWidth
                label={t('product.filtersModal.statusField')}
                select
                value={field.value}
                onChange={field.onChange}
            >
                <MenuItem key={""} value={""}/>
                {productStatusList?.map((s) => (
                    <MenuItem key={s.statusName} value={s.statusCode}>
                        {s.statusName}
                    </MenuItem>
                ))}
            </TextField>
        ),
        segment: (field: any) => (
            <TextField
                fullWidth
                label={t('product.filtersModal.segmentField')}
                select
                value={field.value}
                onChange={(e) => {
                    field.onChange(e)
                    setCategoryValues('')
                    setSegmentValue(e.target.value)
                }}
            >
                <MenuItem key={""} value={""}/>
                {segments?.map((s) => (
                    <MenuItem key={s.segmentCode} value={s.segmentCode}>
                        {s.segmentName}
                    </MenuItem>
                ))}
            </TextField>
        ),
        category: (field: any) => (
            <TextField
                fullWidth
                label={t('product.filtersModal.categoryField')}
                select
                value={field.value}
                onChange={field.onChange}
                SelectProps={{
                    MenuProps: MenuProps
                }}
            >
                <MenuItem key={""} value={""}/>
                {categories?.filter(c => c.segmentCode === segmentValue)?.map((c) => (
                    <MenuItem key={c.categoryCode} value={c.categoryCode}>
                        {c.categoryName}
                    </MenuItem>
                ))}
            </TextField>
        ),
        productCode: (field: any) => (
            <TextField
                fullWidth
                label={t('product.filtersModal.productCodeField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        name: (field: any) => (
            <TextField
                fullWidth
                label={t('product.filtersModal.nameField')}
                value={field.value}
                onChange={field.onChange}
            />
        )
    }

    const toolbar = {
        renderActions: () =>
            hasScopes('SCOPE_SM_CREATE_PRODUCT')?
            <Button fullWidth  size="small" onClick={() => {
                navigate('/salesmarketing/productcodecreate')
            }}>
                <LibraryAddRounded style={{paddingRight: "10px"}}/>
                {t('product.buttons.newProduct')}
            </Button> :<></>
    }

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "productCode",
            headerName: t('product.listColumns.productCodeHeader'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            tooltipField: "productCode",
            unSortIcon: true,
            minWidth: 180
        },
        {
            field: "segment",
            headerName: t('product.listColumns.segmentHeader'),
            tooltipField: "segment",
            unSortIcon: true
        },
        {
            field: "category",
            headerName: t('product.listColumns.categoryHeader'),
            tooltipField: "category",
            unSortIcon: true
        },
        {
            field: "name",
            headerName: t('product.listColumns.nameHeader'),
            tooltipField: "name",
            unSortIcon: true
        },
        {
            field: "description",
            headerName: t('product.listColumns.descriptionHeader'),
            tooltipField: "description",
            unSortIcon: true
        },
        {
            field: "startDate",
            headerName: t('product.listColumns.startDateHeader'),
            tooltipField: "startDate",
            unSortIcon: true,
            valueFormatter: params => params.value ? dayjs(params.value).format("lll") : ''
        },
        {
            field: "endDate",
            headerName: t('product.listColumns.endDateHeader'),
            tooltipField: "endDate",
            unSortIcon: true,
            valueFormatter: params => params.value ? dayjs(params.value).format("lll") : ''
        },
        /* {
             field: "guaranteeScheme",
             headerName: t('product.listColumns.guaranteeSchemeHeader'),
             tooltipField: "guaranteeScheme",
             unSortIcon: true
         },
         {
             field: "paymentMethod",
             headerName: t('product.listColumns.paymentMethodHeader'),
             tooltipField: "paymentMethod",
             unSortIcon: true
         },
         {
             field: "paymentDueDate",
             headerName: t('product.listColumns.paymentDueDateHeader'),
             tooltipField: "paymentDueDate",
             unSortIcon: true
         },
         {
             field: "billingMode",
             headerName: t('product.listColumns.billingModeHeader'),
             tooltipField: "billingMode",
             unSortIcon: true
         },
         {
             field: "firstInvoiceRecipient",
             headerName: t('product.listColumns.firstInvoiceRecipientHeader'),
             tooltipField: "firstInvoiceRecipient",
             unSortIcon: true
         },
         {
             field: "subsequentInvoiceRecipient",
             headerName: t('product.listColumns.subsequentInvoiceRecipientHeader'),
             tooltipField: "subsequentInvoiceRecipient",
             unSortIcon: true
         },
         {
             field: "paymentTiming",
             headerName: t('product.listColumns.paymentTimingHeader'),
             tooltipField: "paymentTiming",
             unSortIcon: true
         },*/
        {
            field: "status",
            headerName: t('product.listColumns.statusHeader'),
            pinned: 'right',
            lockPosition: 'right',
            maxWidth: 130,
            lockPinned: true,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabelProduct(params.data.status)}
                             sx={{backgroundColor: getStatusColorProduct(params.data.status)}}/>
            }
        },
        {
            field: "_actions",
            headerName: t('product.listColumns.actionHeader'),
            pinned: 'right',
            lockPosition: 'right',
            maxWidth: 130,
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                           window.open('/salesmarketing/productcodeupdate?productCode=' + params.data.productCode, "_blank");
                        }} auth={'SCOPE_SM_UPDATE_PRODUCT'}/>
                       <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                           setOpenDetail(true);
                           setProductCodeToPass(params.data);
                       }} auth={'SCOPE_SM_VIEW_PRODUCT_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    const defaultColDef = {
        resizable: true,
    };

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = Object.assign({}, filters);
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await productCodeActions.listProductCode(filterTemp);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    };

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['productCode'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('product.noRows')}
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    return (
        <>
            <SectionHeader title={t('product.listHeader.title')} subtitle={t('product.listHeader.subTitle')}/>
            <AdvancedFilter validationObj={validationObj} defaultValues={defaultFilters} fields={fields}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    {categories.length > 0 && productStatusList.length > 0 && segments.length > 0 &&
                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                           noRowsOverlayComponent={noRowsOverlayComponent}
                                           onGridReady={(params) => {
                                               params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                           }}
                                           defaultColDef={defaultColDef}/>
                    }
                </Grid>
            </Grid>
            <ProductCodeDetailModal productCode={productCodeToPass} open={openDetail} handleClose={() => {
                setOpenDetail(false);
            }}/>
        </>
    );
}

export default ProductCodeListPage;